import React, { Dispatch, FC, memo, SetStateAction, useContext, useEffect, useState } from 'react';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import {
  buildEmptyWorkflowSettings,
  normalizeWorkflowSettingsForSave,
  validateWorkflowSettings,
} from 'src/entity/ProjectSettingsForm/ui/workflowSettings';
import { WorkflowSettingsBuilder } from 'src/entity/ProjectSettingsForm/ui/WorkflowSettingsBuilder';
import { groupsApi } from 'src/shared/api/groupsApi';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import { setError as setGlobalError, setSuccess as setGlobalSuccess } from 'src/shared/lib/utils';
import { GroupUser } from 'src/shared/types/groups';
import { Button, Modal } from 'src/shared/ui';

interface ProjectWorkflowModalProps {
  setIsOpenWorkflowModal: Dispatch<SetStateAction<boolean>>;
}

export const ProjectWorkflowModal: FC<ProjectWorkflowModalProps> = memo(
  ({ setIsOpenWorkflowModal }) => {
    const { tableSettings, setTableSettings } = useContext(ProjectsContext);
    const [workflowSettings, setWorkflowSettings] = useState(() =>
      tableSettings?.workflowSettings
        ? normalizeWorkflowSettingsForSave(tableSettings.workflowSettings)
        : buildEmptyWorkflowSettings(),
    );
    const [assignableUsers, setAssignableUsers] = useState<GroupUser[]>([]);
    const [isUsersLoading, setIsUsersLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const onClose = () => setIsOpenWorkflowModal(false);

    useEffect(() => {
      let isMounted = true;

      const loadUsers = async () => {
        setIsUsersLoading(true);
        try {
          const { data } = await groupsApi.getUsers();
          if (!isMounted) {
            return;
          }
          setAssignableUsers(data || []);
        } catch (error) {
          console.error('Failed to load assignable workflow users', error);
          if (isMounted) {
            setAssignableUsers([]);
          }
        } finally {
          if (isMounted) {
            setIsUsersLoading(false);
          }
        }
      };

      void loadUsers();

      return () => {
        isMounted = false;
      };
    }, []);

    const handleSave = async () => {
      if (!tableSettings) {
        return;
      }

      const parsedWorkflowSettings = normalizeWorkflowSettingsForSave(workflowSettings);
      const workflowErrors = validateWorkflowSettings(parsedWorkflowSettings);
      if (workflowErrors.length) {
        setGlobalError(`Проверка бизнес-процесса: ${workflowErrors[0]}`);
        return;
      }

      setIsSaving(true);

      try {
        await ProjectsService.upsertFormWorkflow(tableSettings.id, {
          workflowSettings: parsedWorkflowSettings,
        });

        const { data: workflowData } = await ProjectsService.getFormWorkflow(tableSettings.id);

        setTableSettings({
          ...tableSettings,
          workflowSettings: workflowData.workflowSettings || parsedWorkflowSettings,
          workflowCurrentStatus: workflowData.currentStatus,
          workflowReadOnly: workflowData.readOnly,
        });

        setGlobalSuccess('Настройки бизнес-процесса сохранены');
        onClose();
      } catch (error: any) {
        if (error?.response?.data?.error) {
          setGlobalError(error.response.data.error);
        } else if (error instanceof Error && error.message) {
          setGlobalError(error.message);
        } else {
          setGlobalError('Не удалось сохранить настройки бизнес-процесса');
        }
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Modal onClose={onClose}>
        <div className="flex w-[56rem] flex-col items-center">
          <div className="font-gothampro-500 mb-5 w-fit text-xl text-green-100">Бизнес-процесс</div>

          <div className="w-full">
            <WorkflowSettingsBuilder
              value={workflowSettings}
              onChange={setWorkflowSettings}
              assignableUsers={assignableUsers}
              isUsersLoading={isUsersLoading}
            />
          </div>

          <div className="mt-5 flex w-full items-center gap-3">
            <Button variant="green" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Сохранение...' : 'Сохранить'}
            </Button>
            <Button variant="greyLight" onClick={onClose}>
              Отменить
            </Button>
          </div>
        </div>
      </Modal>
    );
  },
);
